import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Ref } from "react";

import { type WorkerShift } from "../api/useGetWorkerShifts";
import { type WorkerStripeAccount } from "../api/useGetWorkerStripeAccount";
import { type SearchResultItem } from "../hooks/useOmnisearchResults";
import { type CopiedItemType } from "../OmnisearchResultsList";
import { WorkerImpersonateActionItem } from "./WorkerImpersonateActionItem";
import { WorkerShiftItem } from "./WorkerShiftItem";
import { WorkerStripeActionItem } from "./WorkerStripeActionItem";

interface ExpandedWorkerContentProps {
  agent: Agent;
  workerId: string;
  resultIndex: number;
  workerStripeAccount: WorkerStripeAccount | undefined;
  workerShifts: WorkerShift[];
  isLoadingWorkerShifts: boolean;
  isLoadingWorkerStripeAccount: boolean;
  workerHasShifts: boolean;
  workerHasStripeAccount: boolean;
  canImpersonateWorker: boolean;
  impersonateDisabledReason?: string;
  selectedIndex: number;
  copiedItem: CopiedItemType | null;
  onSelect: (item: SearchResultItem) => void;
  registerRef: (index: number) => (element: HTMLElement | null) => void;
}

export function ExpandedWorkerContent(props: ExpandedWorkerContentProps) {
  const {
    agent,
    workerId,
    resultIndex,
    workerStripeAccount,
    workerShifts,
    isLoadingWorkerShifts,
    isLoadingWorkerStripeAccount,
    workerHasShifts,
    workerHasStripeAccount,
    canImpersonateWorker,
    impersonateDisabledReason,
    selectedIndex,
    copiedItem,
    onSelect,
    registerRef,
  } = props;

  const impersonateOffset = 1;
  const stripeOffset = workerHasStripeAccount ? 1 : 0;
  const canImpersonate = canImpersonateWorker && agent.phone.length > 0;

  function getImpersonateDisabledReason(): string | undefined {
    if (!canImpersonateWorker) {
      return impersonateDisabledReason;
    }
    if (agent.phone.length === 0) {
      return "Worker does not have a phone number.";
    }
    return undefined;
  }

  const resolvedDisabledReason = getImpersonateDisabledReason();

  return (
    <>
      <WorkerImpersonateActionItem
        ref={registerRef(resultIndex + 1) as Ref<HTMLDivElement>}
        disabled={!canImpersonate}
        disabledReason={resolvedDisabledReason}
        selected={selectedIndex === resultIndex + 1}
        workerName={agent.name}
        onClick={() => {
          onSelect({
            type: "agent-impersonate",
            data: agent,
            workerId,
          });
        }}
      />
      {!isLoadingWorkerStripeAccount &&
        workerHasStripeAccount &&
        isDefined(workerStripeAccount) && (
          <WorkerStripeActionItem
            ref={registerRef(resultIndex + 1 + impersonateOffset) as Ref<HTMLDivElement>}
            stripeAccount={workerStripeAccount}
            selected={selectedIndex === resultIndex + 1 + impersonateOffset}
            copied={
              copiedItem === "stripe" && selectedIndex === resultIndex + 1 + impersonateOffset
            }
            onClick={() => {
              onSelect({
                type: "agent-stripe",
                data: workerStripeAccount,
                workerId,
              });
            }}
          />
        )}
      {!isLoadingWorkerShifts && workerHasShifts && (
        <>
          {workerShifts.map((workerShift, shiftIndex) => {
            const shiftResultIndex =
              resultIndex + 1 + stripeOffset + impersonateOffset + shiftIndex;
            return (
              <WorkerShiftItem
                key={workerShift.id}
                ref={registerRef(shiftResultIndex) as Ref<HTMLDivElement>}
                shift={workerShift}
                selected={selectedIndex === shiftResultIndex}
                copied={copiedItem === "shift" && selectedIndex === shiftResultIndex}
                onClick={() => {
                  onSelect({
                    type: "agent-shift",
                    data: workerShift,
                    workerId,
                  });
                }}
              />
            );
          })}
        </>
      )}
      {!isLoadingWorkerShifts &&
        !isLoadingWorkerStripeAccount &&
        !workerHasShifts &&
        !workerHasStripeAccount && (
          <Box sx={{ paddingLeft: 6, paddingRight: 2, paddingY: 1 }}>
            <Text variant="caption" color="text.secondary">
              No Stripe account or recent shifts
            </Text>
          </Box>
        )}
    </>
  );
}
